import { AdminHttpClient } from "@/app/api/AdminHttpClient"
import { API_PREFIX_TOUR_PATH } from "@/constants/prefix-path-api"
import { message } from "antd"
import { AxiosRequestConfig } from "axios"
import { t } from "i18next"
import { HttpClient } from "../../../../app/api/HttpClient"
import { FilterSearchParams } from "../../../../lib/external-components"

type TourFilter = {
  searchValue?: string
  category?: number
  tag?: string
  destination?: string[]
  tripType?: string[]
  price?: string[]
  difficulty?: string[]
  duration?: string[]
  is_most_popular?: boolean
}

export const fetchTours = async (
  filter: { searchValue?: string; category?: number; tag?: string },
  limit?: number | string,
  sortBy?: string,
) => {
  const params: AxiosRequestConfig["params"] = {
    sort: sortBy ?? "is_new:DESC",
    populate: "*",
    pagination: {
      limit: limit && limit !== "All" ? limit : undefined,
    },
    filters: {
      $and: [
        {
          category: {
            id: {
              $containsi: filter.category,
            },
          },
        },
        {
          tags: {
            slug: {
              $containsi: filter.tag,
            },
          },
        },
        {
          $or: [
            {
              tags: {
                name: {
                  $containsi: filter.searchValue,
                },
              },
            },
            {
              name: {
                $containsi: filter.searchValue,
              },
            },
          ],
        },
      ],
    },
  }

  return await HttpClient.get(`${API_PREFIX_TOUR_PATH}`, { params })
}

export const fetchMostPopularTours = async (limit?: number | string) => {
  const locale = document?.documentElement?.lang || "en"
  const params: AxiosRequestConfig["params"] = {
    sort: "updatedAt:DESC",
    populate: "*",
    pagination: {
      limit: limit && limit !== "All" ? limit : undefined,
    },
    filters: {
      is_most_popular: {
        $eq: true,
      },
    },
    locale: locale,
  }

  return await HttpClient.get(`${API_PREFIX_TOUR_PATH}`, { params })
}

export const fetchTourAll = async (searchTerms: { [key: string]: string } = {}) => {
  const params: FilterSearchParams = {
    populate: "*",
    sort: "createdAt:desc",
    publicationState: "preview",
    pagination: {
      pageSize: "100",
    },
    filters: {},
  }

  const excludedKeys = ["current", "pageSize", "start", "limit", "total", "page"]

  Object.keys(searchTerms).forEach(key => {
    if (!excludedKeys.includes(key)) {
      const value = searchTerms[key]

      if (key === "publicationState") {
        const convertParamStatusPost = value === "draft" || value === "all" ? "preview" : "live"
        params.publicationState = convertParamStatusPost

        if (value === "draft") {
          params.filters.publishedAt = { $null: true }
        } else {
          delete params.filters.publishedAt
        }
      } else if (key === "locale") {
        params.locale = value
      } else if (!excludedKeys.includes(key)) {
        if (value) {
          params.filters[key] = { $containsi: value }
        }
      } else if (value) {
        params.pagination = {
          ...params.pagination,
          [key]: value,
        }
      }
    } else {
      const value = searchTerms[key]
      if (value) {
        params.pagination = {
          ...params.pagination,
          [key]: value,
        }
      }
    }
  })

  try {
    return await HttpClient.get(`${API_PREFIX_TOUR_PATH}`, { params })
  } catch (error) {
    message.error(t("Failed to fetch tour. Please try again"))
  }
}

export const deleteTour = async (id: number) => {
  return await HttpClient.delete(`${API_PREFIX_TOUR_PATH}/${id}`)
}

export const checkSlug = async (slug: string) => {
  const params: AxiosRequestConfig["params"] = {
    filters: {
      slug: { $eq: slug },
    },
    publicationState: "preview",
  }
  return await HttpClient.get(`${API_PREFIX_TOUR_PATH}`, { params })
}

export const createNewTourV2 = async (data: any) => {
  return await AdminHttpClient.post(`${API_PREFIX_TOUR_PATH}`, data)
}

export const updateTourV2 = async (id: number, data: any) => {
  return await AdminHttpClient.put(`${API_PREFIX_TOUR_PATH}/${id}`, data)
}

export const fetchTourV2ById = async (id: number) => {
  return await AdminHttpClient.get(`${API_PREFIX_TOUR_PATH}/${id}?populate=*`)
}

export const fetchToursV2 = async (
  filter: TourFilter,
  limit?: number | string,
  sortBy?: string,
  page?: number,
  pageSize?: number,
) => {
  const locale = typeof document !== "undefined" ? document.documentElement?.lang || "en" : "en"

  const andConds: any[] = []

  // category theo id (đúng kiểu số)
  if (typeof filter.category === "number") {
    andConds.push({ category: { id: filter.category } })
  }

  // destination theo tên
  if (filter.destination?.length) {
    andConds.push({ destinations: { name: { $in: filter.destination } } })
  }

  // tripType - filter theo category.name thay vì tripType
  if (filter.tripType?.length) {
    // Map tripType values sang category names
    const categoryNameMap: { [key: string]: string } = {
      "day-tours": "Day Tours",
      "multiday-tours": "Multiday Tours",
    }

    const categoryNames = filter.tripType.map(t => categoryNameMap[String(t).toLowerCase()]).filter(Boolean) // Loại bỏ các giá trị undefined

    if (categoryNames.length > 0) {
      andConds.push({
        $or: categoryNames.map(name => ({
          category: { name: { $containsi: name } },
        })),
      })
    }
  }

  // difficulty - OR nhiều giá trị
  if (filter.difficulty?.length) {
    andConds.push({
      $or: filter.difficulty.map(d => ({
        difficulty: { $containsi: String(d).toLowerCase() },
      })),
    })
  }

  // duration - OR nhiều giá trị
  if (filter.duration?.length) {
    const durationOr: any[] = []

    for (const range of filter.duration) {
      switch (range) {
        case "1-day":
          durationOr.push({
            $or: [
              { duration: { $containsi: "1 day" } },
              { duration: { $containsi: "a day" } },
              { duration: { $containsi: "1 days" } },
              { duration: { $containsi: "a days" } },
            ],
          })
          break
        case "2-3-days":
          durationOr.push({
            $or: [
              { duration: { $containsi: "2 day" } },
              { duration: { $containsi: "3 day" } },
              { duration: { $containsi: "2 days" } },
              { duration: { $containsi: "3 days" } },
            ],
          })
          break
        case "4-7-days":
          durationOr.push({
            $or: [
              { duration: { $containsi: "4 day" } },
              { duration: { $containsi: "5 day" } },
              { duration: { $containsi: "6 day" } },
              { duration: { $containsi: "7 day" } },
              { duration: { $containsi: "4 days" } },
              { duration: { $containsi: "5 days" } },
              { duration: { $containsi: "6 days" } },
              { duration: { $containsi: "7 days" } },
            ],
          })
          break
        case "8-14-days":
          durationOr.push({
            $or: [
              { duration: { $containsi: "8 day" } },
              { duration: { $containsi: "9 day" } },
              { duration: { $containsi: "10 day" } },
              { duration: { $containsi: "11 day" } },
              { duration: { $containsi: "12 day" } },
              { duration: { $containsi: "13 day" } },
              { duration: { $containsi: "14 day" } },
              { duration: { $containsi: "8 days" } },
              { duration: { $containsi: "9 days" } },
              { duration: { $containsi: "10 days" } },
              { duration: { $containsi: "11 days" } },
              { duration: { $containsi: "12 days" } },
              { duration: { $containsi: "13 days" } },
              { duration: { $containsi: "14 days" } },
            ],
          })
          break
        case "over-14-days":
          durationOr.push({
            $or: [
              { duration: { $containsi: "15 day" } },
              { duration: { $containsi: "16 day" } },
              { duration: { $containsi: "17 day" } },
              { duration: { $containsi: "18 day" } },
              { duration: { $containsi: "19 day" } },
              { duration: { $containsi: "20 day" } },
              { duration: { $containsi: "21 day" } },
              { duration: { $containsi: "22 day" } },
              { duration: { $containsi: "23 day" } },
              { duration: { $containsi: "24 day" } },
              { duration: { $containsi: "25 day" } },
              { duration: { $containsi: "26 day" } },
              { duration: { $containsi: "27 day" } },
              { duration: { $containsi: "28 day" } },
              { duration: { $containsi: "29 day" } },
              { duration: { $containsi: "30 day" } },
              { duration: { $containsi: "15 days" } },
              { duration: { $containsi: "16 days" } },
              { duration: { $containsi: "17 days" } },
              { duration: { $containsi: "18 days" } },
              { duration: { $containsi: "19 days" } },
              { duration: { $containsi: "20 days" } },
              { duration: { $containsi: "21 days" } },
              { duration: { $containsi: "22 days" } },
              { duration: { $containsi: "23 days" } },
              { duration: { $containsi: "24 days" } },
              { duration: { $containsi: "25 days" } },
              { duration: { $containsi: "26 days" } },
              { duration: { $containsi: "27 days" } },
              { duration: { $containsi: "28 days" } },
              { duration: { $containsi: "29 days" } },
              { duration: { $containsi: "30 days" } },
            ],
          })
          break
      }
    }

    if (durationOr.length) andConds.push({ $or: durationOr })
  }

  if (filter.price?.length) {
    const priceOr: any[] = []

    for (const range of filter.price) {
      switch (range) {
        case "under-100":
          priceOr.push({ price_overview: { $lt: 100 } })
          break
        case "100-300":
          priceOr.push({ price_overview: { $gte: 100, $lte: 300 } })
          break
        case "300-500":
          priceOr.push({ price_overview: { $gte: 300, $lte: 500 } })
          break
        case "over-500":
          priceOr.push({ price_overview: { $gt: 500 } })
          break
        case "under-2500000":
          priceOr.push({ price_overview: { $lt: 2500000 } })
          break
        case "2500000-7500000":
          priceOr.push({ price_overview: { $gte: 2500000, $lte: 7500000 } })
          break
        case "7500000-12500000":
          priceOr.push({ price_overview: { $gte: 7500000, $lte: 12500000 } })
          break
        case "over-12500000":
          priceOr.push({ price_overview: { $gt: 12500000 } })
          break
      }
    }

    if (priceOr.length) andConds.push({ $or: priceOr })
  }

  if (filter.searchValue?.trim()) {
    const q = filter.searchValue.trim()

    // Nếu searchValue là "is_new", lọc theo trường is_new = true
    if (q.toLowerCase() === "is_new") {
      andConds.push({ is_new: { $eq: true } })
    }
    if (q.toLowerCase() === "is_most_popular") {
      andConds.push({ is_most_popular: { $eq: true } })
    } else {
      // Ngược lại, tìm kiếm bình thường theo name và location
      andConds.push({
        $or: [{ name: { $containsi: q } }, { normalize_name: { $containsi: q } }, { location: { $containsi: q } }],
      })
    }
  }

  if (filter.is_most_popular === true) {
    andConds.push({ is_most_popular: { $eq: true } })
  }

  if (filter.tag) {
    andConds.push({ tags: { name: { $containsi: filter.tag } } })
  }

  const params: AxiosRequestConfig["params"] = {
    sort: sortBy ?? "is_new:DESC,is_most_popular:DESC",
    populate: "*",
    locale,
    publicationState: "live",
  }

  // Ưu tiên pagination với page và pageSize nếu được cung cấp
  if (page !== undefined && pageSize !== undefined) {
    params.pagination = { page, pageSize }
  } else if (limit && limit !== "All") {
    params.pagination = { limit: Number(limit) }
  }

  if (andConds.length) params.filters = { $and: andConds }

  return HttpClient.get(`${API_PREFIX_TOUR_PATH}`, {
    params,
    // Nếu gặp vấn đề serialize deep object, bật dòng dưới:
    // paramsSerializer: p => qs.stringify(p, { encodeValuesOnly: true }),
  })
}

export const fetchAllTourV2s = async () => {
  const params = {
    populate: "*",
    sort: "updatedAt:desc",
    pagination: {
      limit: 1000,
    },
  }
  return await HttpClient.get(`${API_PREFIX_TOUR_PATH}`, { params })
}
